import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FileSignature, KeyRound, Mail, ShieldCheck, Smartphone } from "lucide-react";
import { MfaSettingsPanel } from "#/features/auth/components/MfaSettingsPanel";
import { EmployeeSignaturePanel } from "#/features/employees/components/EmployeeSignaturePanel";
import { useAuth } from "#/context/AuthProvider";

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AccountSecurityPage() {
  const [searchParams] = useSearchParams();
  const setupRequired = searchParams.get("setup") === "required";
  const { mfaEnabled } = useAuth();
  const navigate = useNavigate();

  // Once MFA is enabled (user just finished setup), unblock them.
  useEffect(() => {
    if (setupRequired && mfaEnabled === true) {
      navigate("/dashboard", { replace: true });
    }
  }, [setupRequired, mfaEnabled, navigate]);

  if (setupRequired) {
    return <MfaOnboarding />;
  }

  // ── Standard security settings page ─────────────────────────────────────
  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Account security</h1>
        <p className="mt-1 text-sm text-slate-500">
          Manage your password and two-factor authentication settings.
        </p>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
            <KeyRound className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">Password</p>
            <p className="mt-0.5 text-xs text-slate-500">
              Change your account password regularly to stay secure.
            </p>
            <Link
              to="/change-password"
              className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Change password
            </Link>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="mb-5 flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">
              Two-factor authentication (MFA)
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              Require a second verification step at sign-in using an authenticator app or email OTP.
            </p>
          </div>
        </div>
        <MfaSettingsPanel />
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="mb-1 flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
            <FileSignature className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">E-signature</p>
          </div>
        </div>
        <EmployeeSignaturePanel mode="self" />
      </section>
    </div>
  );
}

// ── Onboarding flow (shown when ?setup=required) ──────────────────────────────

type OnboardingStep = "choose" | "totp" | "email";

function MfaOnboarding() {
  const [step, setStep] = useState<OnboardingStep>("choose");

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-start justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
            <ShieldCheck className="h-8 w-8" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Secure your account</h1>
          <p className="mt-2 text-sm text-slate-500">
            Two-factor authentication is required before you can access EATTA EMS.
            Choose how you'd like to verify your identity at each sign-in.
          </p>
        </div>

        {step === "choose" && <MethodChoice onChoose={setStep} />}
        {step === "totp"   && (
          <MfaSettingsPanel
            initialPanel="totp-setup"
          />
        )}
        {step === "email"  && (
          <MfaSettingsPanel
            initialPanel="email-setup"
          />
        )}

        {/* Back link when in a setup panel */}
        {step !== "choose" && (
          <button
            onClick={() => setStep("choose")}
            className="mt-4 w-full rounded-2xl border border-slate-200 bg-white py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
          >
            ← Choose a different method
          </button>
        )}
      </div>
    </div>
  );
}

function MethodChoice({ onChoose }: { onChoose: (s: OnboardingStep) => void }) {
  return (
    <div className="space-y-4">
      {/* TOTP card */}
      <button
        onClick={() => onChoose("totp")}
        className="group w-full rounded-2xl border-2 border-slate-200 bg-white p-5 text-left transition hover:border-emerald-500 hover:bg-emerald-50/50"
      >
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 transition group-hover:bg-emerald-200">
            <Smartphone className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-slate-900">Authenticator App</p>
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                Recommended
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Scan a QR code with Google Authenticator, Authy, or any TOTP app.
              Works offline — no email needed at sign-in.
            </p>
            <p className="mt-2 text-xs font-medium text-emerald-700 group-hover:underline">
              Set up with QR code →
            </p>
          </div>
        </div>
      </button>

      {/* Email card */}
      <button
        onClick={() => onChoose("email")}
        className="group w-full rounded-2xl border-2 border-slate-200 bg-white p-5 text-left transition hover:border-blue-400 hover:bg-blue-50/50"
      >
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600 transition group-hover:bg-blue-200">
            <Mail className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-slate-900">Email OTP</p>
            <p className="mt-1 text-sm text-slate-500">
              Receive a one-time code by email at each sign-in.
              No app required — works anywhere you have inbox access.
            </p>
            <p className="mt-2 text-xs font-medium text-blue-700 group-hover:underline">
              Enable email OTP →
            </p>
          </div>
        </div>
      </button>
    </div>
  );
}
